import React from 'react';
import { 
    TextField, 
    Button, 
    Checkbox, 
    FormControlLabel, 
    Grid, 
    Box, 
    Tooltip,
    Typography, 
    Alert,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FaceIcon from '@mui/icons-material/Face';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import axios from 'axios';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label, disabled, helpText, type = 'text' }) => {
    return (
        <SettingItem helpText={helpText}>
            <TextField
                fullWidth
                size="medium"
                label={label}
                name={name}
                value={value || ''}
                onChange={onChange}
                variant="outlined"
                disabled={disabled}
                type={type}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '0.95rem' },
                        py: { xs: 1.5, sm: 1.25 }
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                    }
                }}
            />
        </SettingItem>
    );
};

// Responsive Checkbox Component
const ResponsiveCheckbox = ({ name, checked, onChange, label, disabled, helpText, warning }) => {
    return (
        <SettingItem helpText={helpText}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox 
                            name={name}
                            checked={checked || false}
                            onChange={onChange}
                            disabled={disabled}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: { xs: 24, sm: 24 }
                                }
                            }}
                        />
                    }
                    label={
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                color: disabled ? 'text.disabled' : 'text.primary'
                            }}
                        >
                            {label}
                        </Typography>
                    }
                    sx={{
                        m: 0,
                        flex: 1,
                        '& .MuiFormControlLabel-label': {
                            flex: 1
                        }
                    }}
                />
                {warning && (
                    <Tooltip title={warning}>
                        <WarningIcon color='error' sx={{ ml: 1, fontSize: 20 }} />
                    </Tooltip>
                )}
            </Box>
        </SettingItem>
    );
};

const FaceRecognitionTab = ({ 
    settings, 
    handleInputChange,
    globalTenantId,
    disableFaceProfileAliases,
    masterFaceProfileAliases,
    masterTenantNameForFaceProfiles
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* About Face Recognition */}
            <SettingsSection title="About Face Recognition" icon={InfoIcon}>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        lineHeight: 1.6
                    }}
                >
                    Face Recognition allows the kiosk to automatically recognise enrolled faces and grant access without user interaction. 
                    Faces must be enrolled using the kiosk's face enrolment mode.
                </Typography>
            </SettingsSection>

            {/* Face Recognition Settings */}
            <SettingsSection title="Face Recognition Settings" icon={FaceIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingEnableFaceRecognition"
                            checked={settings.SettingEnableFaceRecognition}
                            onChange={handleInputChange}
                            label="Enable Face Recognition"
                            helpText="Enables the face recognition functionality on this kiosk"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingIncludeFaceRecognitionButton"
                            checked={settings.SettingIncludeFaceRecognitionButton}
                            onChange={handleInputChange}
                            label="Include Face Recognition button UI (recommended)"
                            disabled={!settings.SettingEnableFaceRecognition}
                            helpText="Shows a button with a visual guide to help users position their face. If disabled, faces are recognized automatically."
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowAliasesForFaceProfiles"
                            checked={masterFaceProfileAliases ?? (settings.SettingAllowAliasesForFaceProfiles || false)}
                            onChange={handleInputChange}
                            label="Enable aliases for face profiles"
                            disabled={!settings.SettingEnableFaceRecognition || disableFaceProfileAliases}
                            helpText="Assign face profiles to Aliases to notify specific people when a face is recognized."
                            warning={disableFaceProfileAliases ? `Face Profiles are shared from ${masterTenantNameForFaceProfiles}. Switch to that kiosk to change this setting.` : null}
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Configuration */}
            <SettingsSection title="Configuration" icon={SettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingFaceRecognitionSecondsBeforeReReads"
                            value={settings.SettingFaceRecognitionSecondsBeforeReReads}
                            onChange={handleInputChange}
                            label="Wait time (seconds) between recognitions"
                            type="number"
                            disabled={!settings.SettingEnableFaceRecognition}
                            helpText="Time before the same face can be recognized again. Helps prevent duplicate recognitions."
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Face Enrolment Mode */}
            <SettingsSection title="Face Enrolment Mode" icon={PersonAddIcon}>
                <Box sx={{ mb: 2 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                            lineHeight: 1.6,
                            mb: 2
                        }}
                    >
                        Use the buttons below to control the kiosk's face enrolment mode. When in enrolment mode, 
                        the kiosk will display a camera interface where people can have their faces captured and stored.
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Button 
                            variant="contained" 
                            color="success"
                            fullWidth
                            disabled={!settings.SettingEnableFaceRecognition}
                            onClick={async () => {
                                try {
                                    await axios.post('/api/face-enrollment-mode/enter', { tenantId: globalTenantId });
                                    alert('Kiosk is now in face enrolment mode. Go to the kiosk to enrol faces.');
                                } catch (error) {
                                    console.error('Error entering enrolment mode:', error);
                                    alert('Failed to enter enrolment mode: ' + (error.response?.data?.message || error.message));
                                }
                            }}
                            sx={{ 
                                py: { xs: 1.5, sm: 1.25 },
                                fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                textTransform: 'none'
                            }}
                        >
                            Put Kiosk in Face Enrolment Mode
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button 
                            variant="outlined" 
                            color="error"
                            fullWidth
                            disabled={!settings.SettingEnableFaceRecognition}
                            onClick={async () => {
                                try {
                                    await axios.post('/api/face-enrollment-mode/exit', { tenantId: globalTenantId });
                                    alert('Kiosk has exited face enrolment mode.');
                                } catch (error) {
                                    console.error('Error exiting enrolment mode:', error);
                                    alert('Failed to exit enrolment mode: ' + (error.response?.data?.message || error.message));
                                }
                            }}
                            sx={{ 
                                py: { xs: 1.5, sm: 1.25 },
                                fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                textTransform: 'none'
                            }}
                        >
                            Exit Face Enrolment Mode
                        </Button>
                    </Grid>
                </Grid>

                <Alert 
                    severity="info" 
                    sx={{ 
                        mt: 3,
                        '& .MuiAlert-message': {
                            width: '100%'
                        }
                    }}
                >
                    <Typography 
                        variant="body2" 
                        component="div"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        <strong>Enrolment Process:</strong>
                        <Box 
                            component="ol" 
                            sx={{ 
                                m: 0, 
                                pl: { xs: 2.5, sm: 3 },
                                mt: 1,
                                '& li': {
                                    mb: 0.5,
                                    '&:last-child': { mb: 0 }
                                }
                            }}
                        >
                            <li>Click "Put Kiosk in Face Enrolment Mode"</li>
                            <li>Go to the kiosk device</li>
                            <li>Have each person stand in front of the camera</li>
                            <li>Follow the on-screen instructions to capture their face</li>
                            <li>Click "Exit Face Enrolment Mode" when finished</li>
                            <li>Go to Face Profiles screen to assign names and configure access</li>
                        </Box>
                    </Typography>
                </Alert>
            </SettingsSection>
        </Box>
    );
};

export default FaceRecognitionTab;
